/**
 * Type definitions for startup-related data structures.
 *
 * This module defines the core TypeScript interfaces used throughout the admin panel
 * for representing startup information. These types ensure type safety when working
 * with startup data from API responses, form submissions, and component props.
 * The interfaces are designed to be comprehensive yet flexible to accommodate
 * various startup profiles and their detailed information.
 *
 * API Response Structure:
 * The backend currently returns a minimal startup object. As the API evolves,
 * additional fields will be added. Optional fields allow graceful handling
 * of partial data while maintaining type safety.
 */

/**
 * Pagination metadata returned by list endpoints.
 */
export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// Human-readable labels and badge colors for startup stages — single source of truth
export const STAGE_LABELS: Record<string, string> = {
  idea: 'Idea',
  mvp: 'MVP',
  early: 'Early Revenue',
  growth: 'Growth',
  scale: 'Scale',
};

export const STAGE_COLORS: Record<string, string> = {
  idea: 'badge-ghost',
  mvp: 'badge-info',
  early: 'badge-warning',
  growth: 'badge-success',
  scale: 'badge-primary',
};

/** Enriched startup item returned by GET /api/v1/admin/startups */
export interface AdminStartupItem {
  id: string;
  name: string;
  slug?: string | null;
  logoUrl?: string | null;
  ownerName?: string | null;
  ownerEmail?: string | null;
  createdAt: string;
  updatedAt: string;
  // Profile-derived fields
  stage?: string | null;
  industry?: string[];
  countries?: string[] | null;
  businessModel?: string[];
  mrr?: number | null;
  monthlyGrowth?: number | null;
  profitable?: string | null;
  raisingCapital?: string | null;
  priorInvestment?: string | null;
  founderCount?: number;
}

export interface AdminStartupsPage {
  data: AdminStartupItem[];
  meta: PaginationMeta;
}

/** Filter state for the admin startups list */
export interface StartupFilters {
  stage?: string;
  industry?: string[];
  country?: string;
  businessModel?: string;
  revenueRange?: string;
  growthRate?: string;
  profitable?: string;
  raisingCapital?: string;
  priorInvestment?: string;
  fullTimeStatus?: string;
  founderCount?: string;
}
